import React, { useState, useEffect } from 'react'
import _ from 'lodash'
import moment from 'moment'
import { fromJS, isImmutable, List, Map } from 'immutable'
import { v4 as uuid } from 'uuid'
import DynamicIcon from '../../../../../Main/Builder/DynamicIcon'
import { useDispatch, useSelector } from 'react-redux'
import {
  clearPowerTableColState,
  clearPowerTableSubData,
  processTableAction,
} from '../../../../../../store/project/actions'
import { processSFAutoTrigger } from '../../../../../../store/project/processSFAutoTrigger'
import clsx from 'clsx'
import { matchSorter } from 'match-sorter'
import { DefaultColumnFilter } from './FilterUI/DefaultColumnFilter'
import { SliderColumnFilter } from './FilterUI/SliderColumnFilter'
import { NumberRangeColumnFilter } from './FilterUI/NumberRangeColumnFilter'
import { DateRangeColumnFilter } from './FilterUI/DateRangeColumnFilter'
import { SelectColumnFilter } from './FilterUI/SelectColumnFilter'
import ArrowRightIcon from '@material-ui/icons/ArrowRight'
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown'
import { formatTableCell } from '../../../../../../lib/format'
import { EditableCell } from './Components/EditableCellComponent'
import CheckIcon from '@material-ui/icons/Check'
import { setElemValue } from '../../../../../../store/project/setElemValue'
import { processCondRules } from '../../../../../../store/project/processCondRules'
import MomentUtils from '@date-io/moment'
import { MuiPickersUtilsProvider } from '../FormControls/DatePickers'
import { RenderFormControl } from '../FormControls/RenderFormControl'
import { processTableValueRules } from '../../../../../../lib/rules'
import { WebTooltip } from '../../../../../Project/UI/WebTooltip'
import { isSame } from '../../../isSame'
import { useLayoutMode } from '../../../../../../hooks/useLayoutMode'
import { NoImageWrap } from '../../../NoImageWrap'

export const POWER_TABLE_DEF_MSG = {
  updateCountMsg: 'Update {count} selected items',
  Cancel: 'Cancel',
  Apply: 'Apply',
}

const PowerTableInputColumn = ({
  value,
  uid,
  elementClasses,
  PageUID,
  component,
  column,
  row,
  data,
  path,
  isPreview,
}) => {
  const dispatch = useDispatch()
  const defaultIndex = _.find(data, (tableRow) => tableRow.value === row.value)?.index ?? row.index
  const index = _.get(row, ['original', 'index'], defaultIndex)
  const layoutMode = useLayoutMode()
  const hideValid = component.getIn(['props', layoutMode, 'hideValid'], false)
  const hideError = component.getIn(['props', layoutMode, 'hideError'], false)
  const [isChanged, setIsChanged] = useState(false)

  const onValueChange = (event) => {
    setIsChanged(true)
    if (['picklist'].includes(_.get(column, 'meta.type', 'text'))) {
      dispatch(setElemValue('', event.target.value?.value, 'PowerTable', [...path, column.id], true, index))
    } else {
      dispatch(setElemValue('', event.target.value, 'PowerTable', [...path, column.id], true, index))
    }
  }

  const onBlur = (event) => {
    dispatch(
      processTableValueRules(
        uid,
        column.id,
        path,
        index,
        value,
        true,
        component,
        isChanged || _.get(column, 'meta.type', 'text') === 'boolean'
      )
    )
    dispatch(processCondRules(uid, null, [...path, column.id], index))
  }

  let formFieldStatus = useSelector((state) => {
    let status = state.getIn(
      ['formFieldStatuses', ...path, column.id, index],
      fromJS({
        status: 'initial',
      })
    )
    if (!isImmutable(status)) {
      status = fromJS({
        status: 'initial',
      })
    }
    if (status.get('status') == 'success') {
      status = status.set('error', 'Good')
    }
    return status
  }, isSame)

  useEffect(() => {
    setIsChanged(false)
  }, [formFieldStatus])

  let helperText = formFieldStatus.get('error', '')
  if (formFieldStatus.get('status', 'initial') === 'error' && hideError) {
    helperText = ''
  } else if (formFieldStatus.get('status', 'initial') === 'success' && hideValid) {
    helperText = ''
  }

  return (
    <MuiPickersUtilsProvider libInstance={moment} utils={MomentUtils}>
      <RenderFormControl
        label={''}
        themeKey="PowerTableField"
        value={value ?? _.get(column, 'meta.defValue', '')}
        isPowerTable={true}
        onChange={onValueChange}
        index={row.index}
        placeholder={_.get(column, 'meta.placeholder', '')}
        column={column}
        row={row}
        type={_.get(column, 'meta.type', 'text') || 'text'}
        field={column.id}
        widgetUID={uid}
        uid={uid}
        styleKey={elementClasses}
        fullWidth
        PageUID={PageUID}
        onBlur={onBlur}
        isTableInputFormat
        isPreview={isPreview}
        error={formFieldStatus.get('status', 'initial') === 'error'}
        helperText={helperText}
        rulesClasses={{
          fterror: formFieldStatus.get('status', 'initial') === 'error',
          ftsuccess: formFieldStatus.get('status', 'initial') === 'success',
        }}
        captions={component.getIn(['props', 'captions'], Map())}
      />
    </MuiPickersUtilsProvider>
  )
}

const clearExpandTree = (expandedTree, cellID) => {
  let result = {
    ...expandedTree,
    [cellID]: null,
  }
  return result
}

const collectPowerTableColumnId = (component) => {
  const columns = []
  const processAddColumn = (col) => {
    if (col.get('columns', List()).size === 0) {
      switch (col.getIn(['meta', 'role'])) {
        case 'static':
        case 'field':
        case 'input':
          columns.push(col.get('id'))
          break
        default:
          return ''
      }
    } else {
      col.get('columns', List()).map(processAddColumn)
    }
  }
  component.getIn(['props', 'columns'], List()).map(processAddColumn)
  return columns
}

export const getPowerTableInputColumnDefaults = (component) => {
  const columns = {}
  const processAddColumn = (col) => {
    if (col.get('columns', List()).size === 0 && col.getIn(['meta', 'role']) === 'input') {
      columns[col.get('id')] = col.getIn(['meta', 'defValue'], '')
    } else {
      col.get('columns', List()).map(processAddColumn)
    }
  }
  component.getIn(['props', 'columns'], List()).map(processAddColumn)
  return columns
}

export const collectPowerTableInputColumn = (component) => {
  const columns = []
  const processAddColumn = (col) => {
    if (col.get('columns', List()).size === 0 && col.getIn(['meta', 'role']) === 'input') {
      columns.push(col.get('id'))
    } else {
      col.get('columns', List()).map(processAddColumn)
    }
  }
  component.getIn(['props', 'columns'], List()).map(processAddColumn)
  return columns
}

export const prepareTableData = (isPreview, component, jsData, selection = [], expanded, layoutMode = 'lg') => {
  if (isPreview) {
    const rows = []
    let keys = _.keys(jsData)
    const colID = _.first(_.map(component.getIn(['props', 'columns'], List()).toJS(), (col) => col?.id))
    const allColumns = collectPowerTableColumnId(component)

    let column = _.get(jsData, 'value', [])
    if (!column || !_.size(column)) {
      column = _.get(jsData, _.first(keys), [])
    }
    keys = _.compact(_.uniq([...keys, ...allColumns]))
    if (_.isArray(column)) {
      const isSelectedAll = selection.length === _.size(column)
      let hashCols = {}
      _.each(component.getIn(['props', 'columns'], List()).toJS(), (col) => {
        hashCols[col.id] = col
      })
      _.each(column, (v, index) => {
        const isSelected = selection.includes(jsData?.value?.[index])
        let row = { index, isSelectedAll, isSelected, isSelected, expanded }

        _.each(keys, (key) => {
          row[key] = _.get(jsData, [key, index], '')
          if (_.get(hashCols, [key, 'meta', 'role'], '') === 'static' && !row[key]) {
            row[key] = _.get(hashCols, [key, 'meta', 'static'], '')
          }
        })
        rows.push(row)
      })
    } else {
      if (column !== '') {
        const isSelected = selection.indexOf(jsData?.value) !== -1
        let row = { index: 0, isSelected, isSelectedAll: isSelected, expanded }
        _.each(keys, (key) => {
          row[key] = jsData[key] ?? ''
        })
        rows.push(row)
      }
    }
    return rows
  } else {
    let fieldsInRow = {}
    const processAddColumn = (col) => {
      if (_.size(_.get(col, 'columns', [])) === 0) {
        switch (_.get(col, ['meta', 'role'])) {
          case 'static':
            return _.get(col, ['meta', 'static'], '')
          case 'input':
            return _.get(col, ['meta', 'placeholder'], '')
          case 'field':
            fieldsInRow[col.id] = col.Header ?? ''
            break
          default:
            return ''
        }
      } else {
        _.each(_.get(col, 'columns', []), processAddColumn)
      }
    }
    _.each(component.getIn(['props', 'columns'], List()).toJS(), processAddColumn)
    return _.times(
      component.getIn(['props', layoutMode, 'allowAutoGrow'], component.getIn(['props', 'allowAutoGrow'], false))
        ? 4
        : 24,
      (n) => fieldsInRow
    )
  }
}

const getOnClickAction = (row, column, dispatch, path, isPrevent = true, layoutMode) => {
  const action = column.getIn(['meta', 'actions'], List())
  return (event) => {
    if (isPrevent) {
      event.preventDefault()
      if (layoutMode != 'xs' && layoutMode != 'ss' && layoutMode != 'md') {
        event.stopPropagation()
      }
    }
    let triggerIndex = row.customRowIndex - 1
    if (row.customRowIndex == row.index) {
      triggerIndex = row.index
    }
    dispatch(
      processTableAction(
        action.toJS(),
        path,
        _.get(row, ['original', 'index'], triggerIndex),
        'PowerTable',
        _.get(row, ['original', 'value']),
        event.clientY
      )
    )
    dispatch(processSFAutoTrigger())
  }
}

const processFilterProps = (column) => {
  let options = {}

  switch (column.getIn(['meta', 'filterCtrl'], 'text')) {
    case 'text':
      options.Filter = DefaultColumnFilter
      break
    case 'slider':
      options.Filter = SliderColumnFilter
      break
    case 'numberRange':
      options.Filter = NumberRangeColumnFilter
      break
    case 'dateRange':
      options.Filter = DateRangeColumnFilter
      break
    case 'select':
      options.Filter = SelectColumnFilter
      break
    default:
      options.Filter = DefaultColumnFilter
      break
  }

  if (column.getIn(['meta', 'filterFn'], '') !== '') {
    options.filter = column.getIn(['meta', 'filterFn'], '')
  }

  return options
}

const getDefaultColType = (column) => {
  let type = column.getIn(['meta', 'field', 'type'])
  if (type == 'reference') {
    column.getIn(['meta', 'field', 'refs'], List()).map((field) => {
      if (field.getIn(['field', 'value'])) {
        type = field.getIn(['field', 'type'])
      }
    })
  }

  switch (type) {
    case 'boolean':
      return 'boolean'
    case 'datetime':
    case 'time':
    case 'date':
      return 'datetime'
    case 'text':
    case 'textarea':
      return 'text'
    case 'double':
    case 'currency':
    case 'number':
    case 'percent':
    case 'int':
      return 'number'
    default:
      return 'text'
  }
}

const getFooterSettings = (column) => {
  return function (info) {
    const footer = column.getIn(['meta', 'footer'], List())
    if (footer.size === 0) {
      return ''
    }
    return (
      <div style={{ flexDirection: 'column', display: 'flex', justifyContent: 'center' }}>
        {footer.map((format) => {
          const type = format.getIn(['meta', 'format', 'type'], getDefaultColType(column))
          const convertType =
            (nanVal = NaN) =>
            (value) => {
              try {
                switch (type) {
                  case 'text':
                    return String(value)
                  case 'datetime': {
                    let date = new Date(value)
                    if (date) {
                      return date.valueOf()
                    } else {
                      return value
                    }
                  }
                  case 'number': {
                    //support Currency type strings (eg. '$123,123.44' = 123123.44)
                    const num = _.isString(value) ? Number(value.replace(/[^0-9.-]+/g, '')) : parseFloat(value)

                    if (_.isNaN(num)) {
                      return nanVal
                    } else {
                      return num
                    }
                  }
                }
              } catch (error) {
                return value
              }
            }

          if (format.getIn(['type'], 'static') === 'static') {
            let text = format.getIn(['text'], '') || ''
            return <div>{formatTableCell(text, format.getIn(['meta'], Map()).toJS())}</div>
          } else {
            let rows = _.map(info.rows, (row) => _.get(row, ['original', column.get('id')], ''))
            if (info.state.expanded && _.size(_.keys(info.state.expanded)) > 0) {
              let keys = _.keys(info.state.expanded)
              rows = []
              _.each(keys, function (key) {
                if (key) {
                  let data = _.get(info, ['groupedRowsById', key, 'subRows'], [])
                  rows = rows.concat(_.map(data, (row) => _.get(row, ['original', column.get('id')], '')))
                }
              })
            }
            let result = ''
            switch (format.get('function')) {
              case 'count':
                result = _.size(rows)
                break
              case 'sum': {
                result = _.sumBy(rows, convertType(0))
                break
              }
              case 'min':
                result = _.minBy(rows, convertType(0))
                break
              case 'max':
                result = _.maxBy(rows, convertType(0))
                break
              case 'mean':
                result = _.meanBy(rows, convertType(0))
                break
            }
            if (result === null || result === undefined) {
              result = ''
            }
            return <div>{formatTableCell(result, format.getIn(['meta'], Map()).toJS())}</div>
          }
        })}
      </div>
    )
  }
}

const formatSortDateValue = function (value, col, source) {
  let val = value
  if (source === 'report') {
    if (col && col.getIn(['meta', 'format', 'value'])) {
      val = moment(value, col.getIn(['meta', 'format', 'value'])).format()
      if (val == 'Invalid date') {
        val = moment(val, 'YYYY-MM-DD').format()
      }
    } else {
      val = moment(val).format()
    }
  } else {
    if (col && col.getIn(['meta', 'format', 'value'])) {
      let formatTime = 'YYYY-MM-DD'
      if (col.getIn(['meta', 'field', 'type'], '') == 'datetime') {
        formatTime = 'YYYY-MM-DDTHH:mm:ss'
      }
      val = moment(val, formatTime).format()
      if (val == 'Invalid date') {
        val = moment(value, col.getIn(['meta', 'format', 'value'])).format()
      }
    } else {
      val = moment(val).format()
    }
  }
  if (val == 'Invalid date') {
    return 0
  }
  val = new Date(val).getTime()
  return val
}

const formatSortTimeValue = function (val) {
  val = String(val).split(':')
  let hours = parseInt(val[0])
  let minutes = parseInt(val[1])
  if (_.isNaN(hours) || _.isNaN(minutes)) {
    return 0
  }
  return hours * 60 + minutes
}

export const prepareTableColumns = (
  component,
  path,
  elementClasses,
  layoutMode = 'lg',
  modeWidths = {},
  allowSummary = false,
  store
) => {
  const processColumn = (column) => {
    let sortType = 'alphanumeric'

    if (
      component.getIn(['props', layoutMode, 'allowSorting'], component.getIn(['props', 'allowSorting'], true)) &&
      column.getIn(['meta', 'device', layoutMode, 'allowSort'], column.getIn(['meta', 'allowSort'], false))
    ) {
      let type = column.getIn(['meta', 'field', 'type'], '')
      if (type === 'reference') {
        type = column.getIn(['meta', 'field', 'refs'], List()).reduce((r, v) => {
          if (v.get('object') && v.getIn(['field', 'value'])) {
            return v.getIn(['field', 'type'], type)
          }
          return r
        }, type)
      }
      if (['date', 'time', 'datetime'].indexOf(type) > -1) {
        sortType = (rowA, rowB, columnId, isDesc) => {
          let val1 = _.get(rowA, ['original', columnId])
          let val2 = _.get(rowB, ['original', columnId])
          switch (type) {
            case 'date':
            case 'datetime':
              val1 = formatSortDateValue(val1, column, component.getIn(['props', 'source']))
              val2 = formatSortDateValue(val2, column, component.getIn(['props', 'source']))
              break
            case 'time':
              val1 = formatSortTimeValue(val1)
              val2 = formatSortTimeValue(val2)
              break
          }
          let result
          if (_.isNaN(val1) || _.isNaN(val2)) {
            return 0
          }
          if (val1 === 0) {
            val1 = Number.MIN_SAFE_INTEGER
          }
          if (val2 === 0) {
            val2 = Number.MIN_SAFE_INTEGER
          }
          if (val1 >= val2) {
            result = 1
          } else {
            result = -1
          }
          return result
        }
      } else if (['double', 'currency', 'number', 'percent', 'int'].includes(type)) {
        sortType = (rowA, rowB, columnId, isDesc) => {
          let val1 = _.get(rowA, ['original', columnId])
          let val2 = _.get(rowB, ['original', columnId])

          val1 = _.isString(val1) ? Number(val1.replace(/[^0-9.-]+/g, '')) : parseFloat(val1)
          val2 = _.isString(val2) ? Number(val2.replace(/[^0-9.-]+/g, '')) : parseFloat(val2)

          let result
          if (_.isNaN(val1) || _.isNaN(val2)) {
            return 0
          }
          if (val1 >= val2) {
            result = 1
          } else {
            result = -1
          }
          return result
        }
      } else {
        sortType = 'alphanumeric'
      }
    }

    const accessor = (row, index) => {
      if (!row) {
        return ''
      }
      switch (column.getIn(['meta', 'role'])) {
        case 'input': {
          let rawValue = _.get(row, column.get('id'), column.getIn(['meta', 'defValue'], ''))
          if (_.isArray(rawValue)) {
            rawValue = rawValue.join(', ')
          } else if (_.isPlainObject(rawValue)) {
            rawValue = _.compact(_.values(rawValue)).join(', ')
          }
          return rawValue
        }
        case 'static':
          return _.get(row, column.get('id'), column.getIn(['meta', 'static'], ''))
        case 'field':
          let rawValue = _.get(row, column.get('id'), '')
          if (_.isArray(rawValue)) {
            rawValue = rawValue.join(', ')
          } else if (_.isPlainObject(rawValue)) {
            rawValue = _.compact(_.values(rawValue)).join(', ')
          }
          let state = store.getState()
          const rowInlineEdit = state.getIn(
            ['rules', ...path, 'inline', `${column.get('id')}`, `r${_.get(row, ['original', 'index'], row?.index)}`],
            null
          )
          const isAllowEdit = rowInlineEdit !== null ? rowInlineEdit : column.getIn(['meta', 'allowEdit'], false)
          if (isAllowEdit) {
            return rawValue
          } else {
            return formatTableCell(rawValue, column.getIn(['meta'], Map()).toJS())
          }
          return rawValue
        case 'index':
          return row.index + 1
        default:
          return ''
      }
    }

    const widthInfo = modeWidths[column.get('id')]
      ? { width: modeWidths[column.get('id')] }
      : modeWidths.col
        ? { width: modeWidths.col }
        : {}
    let isVisible = column.getIn(['isVisible', layoutMode], true) && !column.getIn(['meta', 'hideCol'], false)
    if (isVisible === undefined) {
      isVisible = true
    }

    const toolTipGetter = (row) => {
      if (column.getIn(['meta', 'tooltipField', 'value'])) {
        switch (column.getIn(['meta', 'role'])) {
          case 'static':
            return column.getIn(['meta', 'static'], '')
          case 'field':
            let rawValue = _.get(row, `${column.get('id')}-tooltip`, '')
            let formattedTableCell = rawValue
            if (_.isArray(rawValue) || _.isPlainObject(rawValue)) {
              if (_.isArray(rawValue)) {
                rawValue = rawValue.join(', ')
              } else if (_.isPlainObject(rawValue)) {
                rawValue = _.compact(_.values(rawValue)).join(', ')
              }
              formattedTableCell = formatTableCell(rawValue, column.getIn(['meta'], Map()).toJS())
            }
            return formattedTableCell
          case 'index':
            return row.index + 1
          default:
            return ''
        }
      } else {
        return accessor(row)
      }
    }

    let isAllowFiltering = false
    if (component.getIn(['props', layoutMode, 'allowFiltering'], component.getIn(['props', 'allowFiltering'], false))) {
      if (column.getIn(['meta', 'device', layoutMode, 'allowFilter'], column.getIn(['meta', 'allowFilter'], false))) {
        isAllowFiltering = true
      }
    }
    const updateable = column.getIn(['meta', 'field', 'updateable'])
    return {
      id: column.get('id'),
      Header: ['text'].indexOf(column.getIn(['meta', 'role'])) === -1 ? column.get('Header', '') : '',
      ...(allowSummary
        ? {
            Footer: getFooterSettings(column),
          }
        : {}),
      source: component.getIn(['props', 'source']),
      isVisible: isVisible,
      role: column.getIn(['meta', 'role']),
      ...widthInfo,
      titleTooltip: column.getIn(['meta', 'titleTooltip'], '') || '',
      ariaLabel: column.getIn(['meta', 'btnText'], ''),
      toolTipGetter,
      ...(column.get('columns', List()).size === 0
        ? {
            accessor,
            hideTooltip: component.getIn(['props', layoutMode, 'hideTooltip'], false),
            allowFilter: isAllowFiltering,
            filterPlaceholder: component.getIn(
              ['props', layoutMode, 'allowFiltering'],
              component.getIn(['props', 'allowFiltering'], true)
            )
              ? column.getIn(['meta', 'filterPlaceholder'], '')
              : '',
            disableSortBy: component.getIn(
              ['props', layoutMode, 'allowSorting'],
              component.getIn(['props', 'allowSorting'], true)
            )
              ? !column.getIn(['meta', 'device', layoutMode, 'allowSort'], column.getIn(['meta', 'allowSort'], false))
              : true,
            allowInlineEdit:
              column.getIn(['meta', 'allowEdit'], false) &&
              !column.getIn(['meta', 'field', 'calculated'], false) &&
              column.getIn(['meta', 'role']) === 'field' &&
              updateable,
            reportInlineEdit: column.getIn(['meta', 'reportInlineEdit'], Map()),
            fieldType: column.getIn(['meta', 'field', 'type'], 'text'),
            mask: column.getIn(['meta', 'format', 'mask'], ''),
            canGroupBy: component.getIn(
              ['props', layoutMode, 'allowGrouping'],
              component.getIn(['props', 'allowGrouping'], true)
            )
              ? column.getIn(['meta', 'device', layoutMode, 'allowGroup'], column.getIn(['meta', 'allowGroup'], false))
              : false,
            meta: column.getIn(['meta'], Map()).toJS(),
            actions: column.getIn(['meta', 'actions'], false),
            lookup: column.getIn(['meta', 'lookup'], false),
            isHaveValueRules: column.getIn(['meta', 'valueRules'], List()).size > 0,
            sortList: column.getIn(['meta', 'sortList'], ''),
            ...processFilterProps(column),
            allowSubComponent: column.getIn(['meta', 'subCtrl', 'enable']),
            allowHideIcon: column.getIn(['meta', 'subCtrl', 'allowHideIcon']),
            subCtrl: column.getIn(['meta', 'subCtrl'], Map()),
            disableWrap: column.getIn(['meta', 'disableWrap'], false),
            sortType: sortType,
          }
        : { columns: column.get('columns', List()).map(processColumn).toJS(), disableSortBy: true }),
      ...(['icon', 'text'].indexOf(column.getIn(['meta', 'role'])) === -1
        ? {}
        : {
            Icon: ({ row, themeKey }) => {
              const dispatch = useDispatch()
              if (row.isGrouped) {
                return null
              }
              return (
                <WebTooltip
                  title={column.getIn(['meta', 'tooltip'], '')}
                  {...(component.getIn(['props', layoutMode, 'hideTooltip'], false) ? { open: false } : {})}
                  classes={{ tooltip: `ft--theme--${themeKey}--tooltip ${elementClasses}--tooltip` }}>
                  <span>
                    {column.getIn(['meta', 'icon']) === 'custom##custom' && (
                      <span
                        role="button"
                        aria-label={column.getIn(['meta', 'btnText'], '')}
                        onClick={getOnClickAction(
                          row,
                          column,
                          dispatch,
                          path,
                          !column.getIn(['meta', 'subCtrl', 'enable']),
                          layoutMode
                        )}
                        className={clsx('ft--theme--PowerTable--icon custom', `${elementClasses}--icon`)}
                        dangerouslySetInnerHTML={{ __html: column.getIn(['meta', 'htmlIcon'], '') }}
                      />
                    )}
                    {column.getIn(['meta', 'icon']) !== 'custom##custom' && (
                      <DynamicIcon
                        role="button"
                        aria-label={column.getIn(['meta', 'btnText'], '')}
                        icon={column.getIn(['meta', 'icon'])}
                        onClick={getOnClickAction(
                          row,
                          column,
                          dispatch,
                          path,
                          !column.getIn(['meta', 'subCtrl', 'enable']),
                          layoutMode
                        )}
                        className={clsx('ft--theme--PowerTable--icon', `${elementClasses}--icon`)}
                      />
                    )}
                  </span>
                </WebTooltip>
              )
            },
            Text: ({ row, themeKey }) => {
              const dispatch = useDispatch()
              if (row.isGrouped) {
                return null
              }
              return (
                <WebTooltip
                  title={column.getIn(['meta', 'tooltip'], '')}
                  {...(component.getIn(['props', layoutMode, 'hideTooltip'], false) ? { open: false } : {})}
                  classes={{ tooltip: `ft--theme--${themeKey}--tooltip ${elementClasses}--tooltip` }}>
                  <div
                    role="button"
                    aria-label={column.getIn(['meta', 'btnText'], '')}
                    onClick={getOnClickAction(
                      row,
                      column,
                      dispatch,
                      path,
                      !column.getIn(['meta', 'subCtrl', 'enable']),
                      layoutMode
                    )}
                    className={clsx('ft--theme--PowerTable--button', `${elementClasses}--button`)}>
                    {column.getIn(['meta', 'btnText'], '')}
                  </div>
                </WebTooltip>
              )
            },
          }),
      Cell: ({ value, row, themeKey, isPreview, ...rest }) => {
        const dispatch = useDispatch()
        const role = column.getIn(['meta', 'role'])
        if (role == 'index') {
          value = rest.rowIndex
        }
        const rowInlineEdit = useSelector((state) =>
          state.getIn(
            [
              'rules',
              ...rest.path,
              'inline',
              `${column.get('id')}`,
              `r${_.get(row, ['original', 'index'], row.index)}`,
            ],
            null
          )
        )
        let cellProps = {}
        const onIconClick = column.getIn(['meta', 'onIconClick'], false)
        let isAllowEdit =
          rowInlineEdit !== null
            ? rowInlineEdit
            : column.getIn(['meta', 'allowEdit'], false) && column.getIn(['meta', 'role']) === 'field'
        let icon = null
        if (row.canExpand) {
          isAllowEdit = false
        }
        if (['icon', 'text'].indexOf(role) === -1) {
          const allowAction = column.getIn(['meta', 'allowAction'], false)
          if (allowAction && !onIconClick) {
            cellProps.onClick = getOnClickAction(row, column, dispatch, path, false, layoutMode)
            cellProps.role = 'button'
            cellProps['aria-label'] = column.getIn(['meta', 'btnText'], '') || value || ''
          }
          cellProps.style = cellProps.style || {}
          if (
            isImmutable(column.getIn(['meta', 'actions'], false)) &&
            column.getIn(['meta', 'actions'], false)?.size > 0 &&
            !onIconClick
          ) {
            cellProps.style.cursor = 'pointer'
          }
          icon =
            onIconClick && allowAction ? (
              <WebTooltip
                title={column.getIn(['meta', 'tooltip'], '')}
                {...(component.getIn(['props', layoutMode, 'hideTooltip'], false) ? { open: false } : {})}
                classes={{ tooltip: `ft--theme--${themeKey}--tooltip ${elementClasses}--tooltip` }}>
                <span>
                  {column.getIn(['meta', 'icon']) === 'custom##custom' && (
                    <span
                      role="button"
                      aria-label={column.getIn(['meta', 'btnText'], '')}
                      onClick={getOnClickAction(
                        row,
                        column,
                        dispatch,
                        path,
                        !column.getIn(['meta', 'subCtrl', 'enable']),
                        layoutMode
                      )}
                      className={`ft--theme--PowerTable--icon custom ${elementClasses}--icon`}
                      dangerouslySetInnerHTML={{ __html: column.getIn(['meta', 'htmlIcon'], '') }}
                    />
                  )}
                  {column.getIn(['meta', 'icon']) !== 'custom##custom' && (
                    <DynamicIcon
                      role="button"
                      aria-label={column.getIn(['meta', 'btnText'], '')}
                      icon={column.getIn(['meta', 'icon'])}
                      onClick={getOnClickAction(row, column, dispatch, path, false, layoutMode)}
                      className={clsx('ft--theme--PowerTable--icon', `${elementClasses}--icon`)}
                    />
                  )}
                </span>
              </WebTooltip>
            ) : null
          if (onIconClick && allowAction) {
            cellProps.style.display = 'flex'
            cellProps.style.alignItems = 'center'
            cellProps.style.columnGap = '3px'
          }
        }
        if (column.getIn(['meta', 'role']) === 'input') {
          return <PowerTableInputColumn themeKey={themeKey} value={value} row={row} isPreview={isPreview} {...rest} />
        }
        if (isAllowEdit && !row.isGrouped) {
          return <EditableCell value={value} rowInlineEdit={rowInlineEdit} row={row} isPreview={isPreview} {...rest} />
        }
        if (row.isGrouped) {
          icon = null
        }
        if (getDefaultColType(column) == 'boolean') {
          return (
            <div {...cellProps}>
              {String(value) === 'true' ? <CheckIcon /> : ''}
              {icon}
            </div>
          )
        }
        let saveValue = value
        if (saveValue === null) {
          saveValue = ''
        }
        if (row.isGrouped) {
          saveValue = formatTableCell(saveValue, column.getIn(['meta'], Map()).toJS())
        }
        if (column.getIn(['meta', 'format', 'value']) === 'Image' && saveValue) {
          return (
            <div {...cellProps}>
              {!isPreview && <NoImageWrap className={clsx('ft--theme--PowerTable--img', `${elementClasses}--img`)} />}
              {isPreview && (
                <img
                  src={saveValue}
                  alt={column.get('Header') || ''}
                  className={clsx('ft--theme--PowerTable--img', `${elementClasses}--img`)}
                />
              )}
            </div>
          )
        }

        return (
          <div {...cellProps}>
            <div dangerouslySetInnerHTML={{ __html: String(saveValue) }} />
            {icon}
          </div>
        )
      },
      SubComponent: ({
        row,
        cellID,
        cell,
        expanded,
        setExpanded,
        expandedTree,
        setExpandedTree,
        path,
        PageUID,
        uid,
        isPreview,
        themeKey,
        elementClasses,
        allowHideIcon,
      }) => {
        const isExpanded = expanded === `${row.id}:${cellID}`
        const dispatch = useDispatch()
        const elemID = String(uid).indexOf('##') !== -1 ? _.first(String(uid).split('##')) : uid
        const sfItemID = useSelector((state) =>
          isPreview
            ? state
                .getIn(['project', 'Settings', 'sfMapping'], Map())
                .findKey((elem) => elem.get('elemUID') === `${elemID}##${cellID}`)
            : null
        )

        const onClick = React.useCallback(
          (event) => {
            // if (column.allowInlineEdit) {
            //     if (event.target && ['path', 'svg'].indexOf(String(event.target.tagName).toLowerCase()) == -1) {
            //         return false;
            //     }
            // }
            event.preventDefault()
            const allowAction = column.getIn(['meta', 'allowAction'], false)
            if (allowAction) {
              let onClick = getOnClickAction(row, column, dispatch, path, false, layoutMode)
              onClick(event)
            }
            let isNeedGetData = true
            if (expandedTree) {
              _.each(expandedTree, function (val, key) {
                if (val == cellID) {
                  isNeedGetData = false
                }
              })
            }
            if (!isExpanded && isNeedGetData && sfItemID) {
              dispatch(clearPowerTableSubData(sfItemID))

              dispatch(
                processTableAction(
                  [
                    {
                      uid: uuid(),
                      conditions: [],
                      isAction: false,
                      actions: {
                        sfAction: [
                          {
                            items: [
                              {
                                value: sfItemID,
                                order: '',
                              },
                            ],
                          },
                        ],
                      },
                    },
                  ],
                  path,
                  row.index,
                  'PowerTable',
                  _.get(row, ['original', 'value'])
                )
              )
            } else {
              if (expandedTree) {
                _.each(expandedTree, function (val, key) {
                  if (String(val).indexOf(cellID) > -1) {
                    expandedTree[key] = null
                  }
                })
              }
            }
            let isNewExpand = isExpanded ? '' : `${row.id}:${cellID}`
            if (expandedTree && !isNewExpand) {
              window.isManualOpenedTree = true
              requestAnimationFrame(() => {
                window.isManualOpenedTree = false
              })
              setExpandedTree(clearExpandTree(expandedTree, cellID))
            }
            setExpanded(isNewExpand)
          },
          [sfItemID, isExpanded, expandedTree]
        )

        return (
          <>
            <div onClick={onClick} className="ft--theme--PowerTable--subCtrl-btn">
              {!allowHideIcon && (
                <>
                  {!isExpanded && (
                    <ArrowRightIcon
                      className={clsx('collapse-btn', `ft--theme--${themeKey}--extIcon ${elementClasses}--extIcon`)}
                    />
                  )}
                  {isExpanded && (
                    <ArrowDropDownIcon
                      className={clsx('collapse-btn', `ft--theme--${themeKey}--extIcon ${elementClasses}--extIcon`)}
                    />
                  )}
                </>
              )}
              {cell}
            </div>
          </>
        )
      },
    }
  }

  let allCollumns = component.getIn(['props', 'columns'], List())
  if (!List.isList(allCollumns)) {
    allCollumns = List()
  }
  if (allCollumns && allCollumns.filter) {
    allCollumns = allCollumns.filter((row) => {
      if (row && row.getIn(['isVisible', layoutMode], true)) {
        return true
      }
      return false
    })
  }

  return _.compact(allCollumns.map(processColumn).toJS())
}
export const prepareTreeColumns = (component, path, elementClasses, layoutMode = 'lg', modeWidths = {}) => {
  const processColumn = (column) => {
    const accessor = (row, index) => {
      switch (column.getIn(['meta', 'role'])) {
        case 'static':
          return _.get(row, column.get('id'), column.getIn(['meta', 'static'], ''))
        case 'field':
          let rawValue = _.get(row, column.get('id'), '')
          if (_.isArray(rawValue)) {
            rawValue = rawValue.join(', ')
          } else if (_.isPlainObject(rawValue)) {
            rawValue = _.compact(_.values(rawValue)).join(', ')
          }
          return formatTableCell(rawValue, column.getIn(['meta'], Map()).toJS())
        case 'index':
          return index + 1
        default:
          return ''
      }
    }

    const widthInfo = modeWidths[column.get('id')]
      ? { width: modeWidths[column.get('id')] }
      : modeWidths.col
        ? { width: modeWidths.col }
        : {}

    return {
      id: column.get('id'),
      Header: '',
      source: component.getIn(['props', 'source']),
      isVisible: column.getIn(['isVisible', layoutMode], true) && !column.getIn(['meta', 'hideCol'], false),
      role: column.getIn(['meta', 'role']),
      ...widthInfo,
      ...(column.get('columns', List()).size === 0
        ? {
            accessor,
            fieldType: column.getIn(['meta', 'field', 'type'], 'text'),
            iconType: column.getIn(['meta', 'icon', 'role'], 'icon') || 'icon',
            mask: column.getIn(['meta', 'format', 'mask'], ''),
            actions: column.getIn(['meta', 'actions'], false),
            lookup: column.getIn(['meta', 'lookup'], false),
            allowSubComponent: column.getIn(['meta', 'subCtrl', 'enable']),
            subCtrl: column.getIn(['meta', 'subCtrl'], Map()),
          }
        : { columns: column.get('columns', List()).map(processColumn).toJS() }),
      ...(['icon', 'text'].indexOf(column.getIn(['meta', 'role'])) === -1
        ? {}
        : {
            Icon: ({ row, hideTooltip }) => {
              const dispatch = useDispatch()
              return (
                <WebTooltip title={column.getIn(['meta', 'tooltip'], '')} {...(hideTooltip ? { open: false } : {})}>
                  <span>
                    <DynamicIcon
                      role="button"
                      aria-label={column.getIn(['meta', 'btnText'], '')}
                      icon={column.getIn(['meta', 'icon'])}
                      onClick={getOnClickAction(row, column, dispatch, path, false, layoutMode)}
                      className={clsx('ft--theme--TreeView--icon', `${elementClasses}--icon`)}
                    />
                  </span>
                </WebTooltip>
              )
            },
            Text: ({ row, hideTooltip }) => {
              const dispatch = useDispatch()
              return (
                <WebTooltip title={column.getIn(['meta', 'tooltip'], '')} {...(hideTooltip ? { open: false } : {})}>
                  <div
                    role="button"
                    aria-label={column.getIn(['meta', 'btnText'], '')}
                    onClick={getOnClickAction(row, column, dispatch, path, false, layoutMode)}
                    className={clsx('ft--theme--TreeView--button', `${elementClasses}--button`)}>
                    {column.getIn(['meta', 'btnText'], '')}
                  </div>
                </WebTooltip>
              )
            },
          }),
      Cell: ({ value, row, hideTooltip, ...rest }) => {
        const dispatch = useDispatch()
        const role = column.getIn(['meta', 'role'])
        let cellProps = {}
        const onIconClick = column.getIn(['meta', 'onIconClick'], false)
        const isAllowEdit = column.getIn(['meta', 'allowEdit'], false)
        let icon = null
        if (['icon', 'text'].indexOf(role) === -1) {
          const allowAction = column.getIn(['meta', 'allowAction'], false)
          if (allowAction && !onIconClick) {
            cellProps.onClick = getOnClickAction(row, column, dispatch, path, false, layoutMode)
            cellProps.role = 'button'
            cellProps['aria-label'] = column.getIn(['meta', 'btnText'], '') || value || ''
          }
          cellProps.style = cellProps.style || {
            display: 'flex',
            alignItems: 'center',
          }
          if (
            isImmutable(column.getIn(['meta', 'actions'], false)) &&
            column.getIn(['meta', 'actions'], false)?.size > 0
          ) {
            cellProps.style.cursor = 'pointer'
          }
          icon =
            onIconClick && allowAction ? (
              <WebTooltip title={column.getIn(['meta', 'tooltip'], '')} {...(hideTooltip ? { open: false } : {})}>
                <span>
                  {column.getIn(['meta', 'htmlIcon'], '') && !column.getIn(['meta', 'icon']) && (
                    <span
                      role="button"
                      aria-label={column.getIn(['meta', 'btnText'], '')}
                      onClick={getOnClickAction(
                        row,
                        column,
                        dispatch,
                        path,
                        !column.getIn(['meta', 'subCtrl', 'enable']),
                        layoutMode
                      )}
                      className={clsx(`ft--theme--PowerTable--icon custom ${elementClasses}--icon`)}
                      dangerouslySetInnerHTML={{ __html: column.getIn(['meta', 'htmlIcon'], '') }}
                    />
                  )}
                  {column.getIn(['meta', 'icon']) && (
                    <DynamicIcon
                      role="button"
                      aria-label={column.getIn(['meta', 'btnText'], '')}
                      icon={column.getIn(['meta', 'icon'])}
                      onClick={getOnClickAction(row, column, dispatch, path, false, layoutMode)}
                      className={clsx('ft--theme--TreeView--icon', `${elementClasses}--icon`)}
                    />
                  )}
                </span>
              </WebTooltip>
            ) : null
          if (onIconClick && allowAction) {
            cellProps.style.display = 'flex'
            cellProps.style.alignItems = 'center'
            cellProps.style.columnGap = '3px'
          }
        }
        if (isAllowEdit) {
          return <EditableCell value={value} row={row} {...rest} />
        }
        let image = null
        if (column.getIn(['meta', 'icon', 'enable']) && row?.original?.[`${column.get('id')}-icon`]) {
          if ((column.getIn(['meta', 'icon', 'role'], 'icon') || 'icon') === 'icon') {
            image = (
              <DynamicIcon
                icon={row?.original?.[`${column.get('id')}-icon`]}
                className={clsx('ft--theme--TreeView--icon', `${elementClasses}--icon`)}
              />
            )
          } else {
            image = (
              <img
                src={row?.original?.[`${column.get('id')}-icon`]}
                alt="icon"
                className={clsx('ft--theme--TreeView--img', `${elementClasses}--img`)}
              />
            )
          }
        } else if (column.getIn(['meta', 'svgIcon'])) {
          image = (
            <DynamicIcon
              icon={column.getIn(['meta', 'svgIcon'])}
              className={clsx('ft--theme--TreeView--icon', `${elementClasses}--icon`)}
            />
          )
        }
        if (getDefaultColType(column) == 'boolean') {
          return (
            <div {...cellProps}>
              {image}
              {icon}
              {String(value) === 'true' ? <CheckIcon /> : ''}
            </div>
          )
        }
        return (
          <div {...cellProps}>
            {image}
            {icon}
            <div dangerouslySetInnerHTML={{ __html: String(value) }} />
          </div>
        )
      },
      SubComponent: ({
        row,
        cellID,
        cell,
        expanded,
        setExpanded,
        expandedTree,
        setExpandedTree,
        path,
        PageUID,
        uid,
        isPreview,
        column,
      }) => {
        const isExpanded = expanded === `${row.id}:${cellID}`
        const dispatch = useDispatch()
        const elemID = String(uid).indexOf('##') !== -1 ? _.first(String(uid).split('##')) : uid
        const sfItemID = useSelector((state) =>
          isPreview
            ? state
                .getIn(['project', 'Settings', 'sfMapping'], Map())
                .findKey((elem) => elem.get('elemUID') === `${elemID}##${cellID}`)
            : null
        )

        const onClick = React.useCallback(
          (event) => {
            if (column.allowInlineEdit) {
              if (event.target && ['path', 'svg'].indexOf(String(event.target.tagName).toLowerCase()) == -1) {
                return false
              }
            }
            event.preventDefault()
            setExpanded((oldExpanded) => {
              let isExpanded = oldExpanded === `${row.id}:${cellID}`
              let isNeedGetData = true
              if (expandedTree) {
                _.each(expandedTree, function (val, key) {
                  if (val == cellID) {
                    isNeedGetData = false
                  }
                })
              }
              if (!isExpanded && isNeedGetData && isPreview && sfItemID) {
                if (expandedTree && !isExpanded) {
                  setExpandedTree(clearExpandTree(expandedTree, cellID))
                }
                dispatch(clearPowerTableColState(`${elemID}##${cellID}`, PageUID))
                dispatch(
                  processTableAction(
                    [
                      {
                        uid: uuid(),
                        conditions: [],
                        isAction: false,
                        actions: {
                          sfAction: [
                            {
                              items: [
                                {
                                  value: sfItemID,
                                  order: '',
                                },
                              ],
                            },
                          ],
                        },
                      },
                    ],
                    path,
                    row.index,
                    'PowerTable',
                    _.get(row, ['original', 'value'])
                  )
                )
              } else {
              }
              return isExpanded ? '' : `${row.id}:${cellID}`
            })
          },
          [sfItemID]
        )

        return (
          <>
            <div onClick={onClick} className="ft--theme--TreeView--subCtrl-btn">
              {!isExpanded && <ArrowRightIcon className="collapse-btn" />}
              {isExpanded && <ArrowDropDownIcon className="collapse-btn" />}
              {cell}
            </div>
          </>
        )
      },
    }
  }

  return _.compact(component.getIn(['props', 'columns'], List()).map(processColumn).toJS())
}

export function fuzzyTextFilterFn(rows, id, filterValue) {
  return matchSorter(rows, filterValue, { keys: [(row) => row.values[id]] })
}

export const getBuilderDefaultExpandElement = (columns) => {
  let expanded = ''

  const processCol = (col) => {
    if (_.size(col.columns) === 0 && col.allowSubComponent) {
      expanded = `0:${col.id}`
      return false
    } else if (col.columns) {
      _.each(col.columns, processCol)
    }
  }

  _.each(columns, processCol)
  return expanded
}

// Let the table remove the filter if the string is empty
fuzzyTextFilterFn.autoRemove = (val) => !val
